import { ResourcePicker } from '@shopify/app-bridge/actions';
import { FlattenProduct, Product, SelectedItems } from '.';
import { ProductVariant } from './types';

export const flatProducts = (products: Product[]): FlattenProduct[] => {
  const flatten: FlattenProduct[] = [];
  products.forEach((product) => {
    const {
      id,
      title,
      featuredImage,
      images,
      variants,
      productType,
      status,
    } = product;

    const hasVariants = variants.length > 1;
    if (hasVariants) {
      const parentIndex =
        flatten.push({
          id,
          title,
          productType,
          status,
          price: '',
          isGrouper: true,
          imageSrc: featuredImage?.transformedSrc || images?.[0]?.originalSrc,
          hasParent: false,
          child: [],
        }) - 1;
      variants.forEach((variant) => {
        flatten[parentIndex].child.push(variant.id);
        flatten.push({
          ...variant,
          productType,
          status,
          isGrouper: false,
          parent: product,
          hasParent: true,
          child: [],
        });
      });
    } else {
      flatten.push({
        ...variants[0],
        title,
        productType,
        status,
        isGrouper: false,
        imageSrc: featuredImage?.transformedSrc || images?.[0]?.originalSrc,
        hasParent: false,
        child: [],
      });
    }
  });

  return flatten;
};

export const filterVariants = (
  variants: FlattenProduct[],
  name: string,
): FlattenProduct[] => {
  const filter = name.trim().toLowerCase();
  if (filter.length === 0) return variants;
  const filtered: FlattenProduct[] = [];
  let parentAdded = false;
  variants.forEach((v) => {
    if (v.hasParent) {
      if (parentAdded) filtered.push(v);
    } else {
      if (v.title.toLowerCase().includes(filter)) {
        parentAdded = true;
        filtered.push(v);
      } else {
        parentAdded = false;
      }
    }
  });
  return filtered;
};

export const verifySelectedItems = (
  variants: FlattenProduct[],
  selectedItems: SelectedItems,
): SelectedItems => {
  const newSelectedItems = { ...selectedItems };
  variants.forEach((variant) => {
    if (variant.isGrouper) {
      if (variant.child.every((id) => selectedItems[id] === undefined)) {
        // if all child are unselected then unselect the parent
        delete newSelectedItems[variant.id];
      } else if (variant.child.some((id) => !!selectedItems[id] === true)) {
        // if at least a child is selected then select the parent
        newSelectedItems[variant.id] = variant;
      }
    }
  });

  return newSelectedItems;
};

const allChildSelected = (
  variant: FlattenProduct,
  selectedItems: SelectedItems,
): boolean => variant.child.every((id) => selectedItems[id] !== undefined);

export const checkedValue = (
  variants: FlattenProduct[],
  selectedItems: SelectedItems,
  variantId: string,
): boolean | 'indeterminate' => {
  const filtered = variants.filter((v) => v.id === variantId);
  // Product does not exist or is not checked
  if (filtered.length !== 1 || selectedItems[variantId] === undefined)
    return false;

  const variant = filtered[0];
  const haveChild = variant.child.length !== 0;
  return !haveChild || allChildSelected(variant, selectedItems)
    ? true
    : 'indeterminate';
};

export const resourcesToProducts = (
  resources: ResourcePicker.Product[],
): Product[] => resources.map(resourceToProduct);

const resourceToProduct = ({
  id,
  title,
  status,
  hasOnlyDefaultVariant,
  images = [],
  variants = [],
}: ResourcePicker.Product): Product => ({
  id,
  title,
  status,
  hasOnlyDefaultVariant,
  featuredImage: {
    transformedSrc: images[0]?.originalSrc,
  },
  variants: variants.map((v) =>
    resourceVariantToVariant(v as ResourcePicker.ProductVariant),
  ),
});

const resourceVariantToVariant = ({
  id,
  title,
  price,
}: ResourcePicker.ProductVariant): ProductVariant => ({ id, title, price });

/**
 * Takes an array of product with variants and return another array where each row has
 * a single variant, repeating the product information.
 */
export const flatVariants = (products: Product[] = []): Product[] => {
  const flatten: Product[] = [];
  products.forEach((p) =>
    p.variants.forEach((v) => flatten.push({ ...p, variants: [v] })),
  );
  return flatten;
};
